import { onBroadcast } from "./env.ts";
// Native cookie consent manager.
// Manages the first-party basho_consent cookie and updates Google Consent Mode v2.

/**
 * Returns the current consent choice from document.cookie ('granted' | 'denied' | null).
 */
export function getConsentCookie(): "granted" | "denied" | null {
  var match = document.cookie.match(/(?:^|;\s*)basho_consent=(granted|denied)\b/);
  return match ? (match[1] as "granted" | "denied") : null;
}

/**
 * Writes the first-party basho_consent cookie (365 days, SameSite=Lax).
 */
function setConsentCookie(choice: "granted" | "denied") {
  var secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = "basho_consent=" + choice + "; Max-Age=31536000; Path=/; SameSite=Lax" + secure;
}

/**
 * Pushes consent update to Google Consent Mode v2 via gtag.
 */
function updateGtagConsent(choice: "granted" | "denied") {
  if (typeof window.gtag === "function") {
    window.gtag("consent", "update", {
      analytics_storage: choice === "granted" ? "granted" : "denied",
    });
  }
}

/**
 * Persists the user's consent choice and updates analytics tags.
 */
function setConsent(choice: "granted" | "denied") {
  setConsentCookie(choice);
  updateGtagConsent(choice);
  if (typeof window.CustomEvent === "function") {
    window.dispatchEvent(new window.CustomEvent("basho:consent", { detail: { consent: choice } }));
  }
}

/**
 * Animates out and removes the consent banner element.
 */
function dismissBanner(banner: HTMLElement) {
  banner.classList.add("closing");
  var removed = false;
  function remove() {
    if (!removed) {
      removed = true;
      banner.remove();
    }
  }
  banner.addEventListener("transitionend", remove, { once: true });
  setTimeout(remove, 300);
}

/**
 * Returns true if the current viewer is in broadcast / ringside stream mode: the URL's say alone (never a cookie or a
 * storage flag — those once stuck for a year, boot.ts clears what is left of them).
 */
export function isBroadcast(): boolean {
  return onBroadcast();
}

/**
 * Initializes the consent banner if present in the document.
 */
export function initConsent() {
  var banner = document.querySelector(".consent-banner") as HTMLElement | null;
  if (!banner) return;

  if (isBroadcast() || getConsentCookie()) {
    banner.remove();
    return;
  }

  const el = banner;
  banner.addEventListener("click", function (e) {
    var target = e.target as HTMLElement | null;
    var btn = target ? target.closest("button[data-consent]") : null;
    if (!btn) return;
    var action = btn.getAttribute("data-consent");
    if (action === "accept") {
      setConsent("granted");
      dismissBanner(el);
    } else if (action === "decline") {
      setConsent("denied");
      dismissBanner(el);
    }
  });
}
